"use client";
import React from "react";
import { motion } from "framer-motion";
import { FaCheckCircle } from "react-icons/fa";
import { useLocation } from "react-router-dom";

const features = [
  "AI-powered live proctoring ensures real-time monitoring",
  "Flag suspicious activities like impersonation or unauthorized assistance",
  "Automated analysis through AI algorithms to detect unusual movements",
  "Detects devices and suspicious background noises",
  "Triggers instant alerts for review",
  "Multi-camera recording",
  "Can be integrated with traditional CCTV environments",
  "Possibility to enable biometric verification (fingerprint, facial scan)",
  "Cloud-based dashboard for real-time reports, flagged incidents, and analytics",
];

const advantages = [
  "Minimizes cheating risks with AI-driven surveillance, ensuring credible results",
  "Reduces reliance on human invigilators",
  "Cuts operational costs in the long run",
  "Supports concurrent test-takers remote monitoring",
  "High exam monitoring standards with tamper-proof records",
  "Seamless integration and hassle-free setup",
  "Boosts trust in certification processes and improves efficiency",
];

const ExamSurveillance = () => {
  const location = useLocation();
  return (
    <div className={`bg-white text-dark mr-50 ml-50`}>
      {/* Header Section */}
      <section className=" py-5">
        <motion.h1
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="fs-3 fw-bold text-primary"
        >
          Exam Centre Surveillance Solution
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="mt-4 fs-5 text-black"
        >
          Our AI-based exam surveillance solutions work on patented technology
          that can be implemented as an end-to-end solution or layered onto
          existing CCTV systems. We provide a cutting-edge, secure, and scalable
          platform to eliminate malpractice, streamline monitoring, and ensure
          fairness in assessments.
          <br />
          <br />
          Leveraging{" "}
          <span className="fw-semibold text-primary">
            AI-powered proctoring and automated surveillance
          </span>
          , our system detects and prevents cheating while providing holistic
          monitoring, instant alerts, and detailed audit logs. This reduces
          administrative burdens while enhancing exam integrity.
        </motion.p>
      </section>

      {/* Solution Preface */}
      <section className="">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="bg-info bg-opacity-10 border-start border-4 border-info rounded shadow-sm p-4"
        >
          <h3 className="h4 fw-semibold text-info mb-3">Solution Preface</h3>
          <p className="text-black mb-3">
            Unlike traditional invigilation methods prone to human error and
            inefficiency, our automated surveillance platform ensures
            transparency, scalability, and reliability. It supports both remote
            and in-centre exams while offering detailed analytics for
            administrators.
          </p>
          <p className="text-black">
            With seamless integration to existing infrastructure, institutions
            can adopt advanced surveillance with minimal setup efforts while
            maintaining compliance and boosting trust in the certification
            process.
          </p>
        </motion.div>
      </section>

      {/* Key Features */}
      <section className="container py-3">
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="h2 fw-bold text-primary text-center mb-5"
        >
          Key Features
        </motion.h2>

        <div className="row g-4">
          {features.map((feature, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              viewport={{ once: true }}
              className="col-12 col-sm-6 col-md-4 mt-2"
            >
              <div className="bg-white rounded shadow-sm p-3 d-flex gap-2 align-items-start hover-shadow">
                <FaCheckCircle className="text-success mt-1 flex-shrink-0" />
                <span className="text-black">{feature}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Key Advantages */}
      <section
        className="text-white py-5"
        style={{ backgroundColor: "#0a1530" }}
      >
        <div className="container text-center">
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="h2 fw-bold"
          >
            Key Advantages
          </motion.h2>
          <div className="row g-4 mt-4 text-start">
            {advantages.map((adv, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: idx * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="col-12 col-sm-6 col-md-4 mt-2"
              >
                <div className="bg-white bg-opacity-25 p-3 rounded d-flex gap-2 align-items-start">
                  <FaCheckCircle className="text-success mt-1" />
                  <span className="text-white">{adv}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing Statement */}
      <section className="container py-5">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="bg-white rounded shadow-sm p-4"
        >
          <p className="text-black">
            By combining AI-powered monitoring, biometric verification, and
            real-time analytics, our Exam Centre Surveillance Solution delivers
            a future-proof exam integrity system. It empowers institutions to
            uphold credibility, reduce malpractice, and streamline exam
            management with confidence.
          </p>
        </motion.div>
      </section>
    </div>
  );
};

export default ExamSurveillance;
